/*
 * vms-tvcm-mcp.js
 * ---------------------------------------------------------------------------
 * [MCP 2026-09-08] A standalone WebMCP enhancement layer for the TV ceiling-mount finder
 * (/collections/tv-ceiling-mounts). Round 2 of the page-scoped WebMCP coverage, after the cross-reference
 * lookup (assets/vms-xref-mcp.js) and the two calculators (assets/vms-configurator-mcp.js and
 * assets/vms-slanted-wall-mcp.js, both LIVE). Same lane and same shape on purpose: one vocabulary across the
 * VMS layers.
 *
 * WHAT THIS IS. WebMCP lets a page expose MCP-style tools to an in-browser agent (Chrome built-in AI, Claude
 * in Chrome, Cursor) through a modelContext object. This file registers three read-only lookup tools over the
 * ceiling-mount finder. It is a ZERO-IMPACT progressive enhancement: with no modelContext present (every
 * normal shopper's browser today) it registers nothing and changes nothing.
 *
 * THE CONTRACT. This file reads ONLY two things: the <script type="application/json" id="vms-tvcm-data">
 * snapshot that sections/vms-tvcm-configurator.liquid emits after the product grid, and the public read-only
 * window.VMSTvcmPage API that assets/vms-tvcm.js sets up (listConcerns / findMounts / getState). It NEVER
 * scrapes the DOM product cards, and it NEVER touches the concern chips, the size input, the VESA buttons, the
 * URL or anything else a shopper sees. All three tools are read-only. find-mounts runs the SAME matchesWith
 * filter rule the chips run: when window.VMSTvcmPage is present it calls that API (which runs the shipped code
 * path); when the finder's JS did not load it computes the identical rule from the snapshot alone and flags
 * "computed_from": "snapshot". The dataset carries the concern chips (ten, in the page's fixed order; two of
 * them, truss and wood, are CONTENT_ONLY and show guidance on the page but never narrow the list), the size
 * domain, the VESA patterns, and every product on the collection with its card facts.
 *
 * ENTRY POINT, the same Chrome 151 facts measured on the sibling layers (WebMCP flag on, dev preview):
 *   - BOTH document.modelContext AND navigator.modelContext exist; we read either.
 *   - The object is INJECTED LATE, after this deferred script evaluates. A one-shot attach at load therefore
 *     silently no-ops and the page reports "no tools". So the auto-attach below is PATIENT: re-resolve
 *     modelContext at each attempt and retry on DOMContentLoaded, window load, then a timer backoff, giving up
 *     after about 31s.
 *   - Shopify's own platform already registers TEN native storefront tools (browse_store, get_cart,
 *     search_catalog, proceed_to_checkout, ...) in the SAME registry; our three kebab-case tools coexist
 *     beside those snake_case natives (no name clash, no action needed).
 *   - This modelContext has NO unregisterTool. Nothing here calls or depends on it.
 * Registration is gated to a secure, top-level page and is a silent no-op with no modelContext, so a normal
 * shopper is unaffected. attach() is IDEMPOTENT per target (a WeakSet of attached targets), so retries and a
 * manual second call never double-register. window.VMSTvcmMCP = { tools, attach(mcLike) } is exposed so tests
 * and browser verification can drive the same registrations against a stub.
 *
 * TOOL SHAPE. Each tool is { name (kebab-case), description (plain merchant language, inches everywhere),
 * inputSchema (JSON Schema), execute(args) -> { content: [{ type:"text", text:<string> }] } (data is
 * JSON.stringify'd into that text), annotations }. All three are query-only and carry
 * annotations.readOnlyHint = true. Agent params are treated as UNTRUSTED and validated here before any lookup;
 * a bad value is refused with a plain-text reason, never used.
 *
 * [PAGE TOOLS 2026-09-11] The SAME tool objects are also published on window.VMSPageTools (the page-tools
 * registry the storefront chat's page script reads); the registry path calls execute() directly so a
 * failure THROWS, while the WebMCP path shares name / description / inputSchema / annotations / readOnlyHint
 * by reference and wraps ONLY execute so the shim still gets today's error envelope.
 */
(function () {
  'use strict';

  // [PAGE TOOLS 2026-09-11] Shared page-tools registry (window.VMSPageTools). ONE tool object,
  // TWO consumers: this registry, which the storefront chat's own page script reads and executes,
  // and the WebMCP modelContext attach further down. The block is idempotent and byte-identical in
  // all five VMS MCP assets, so load order and double-inclusion are harmless. Registry registration
  // is UNGATED (it runs in every browser and does not wait for modelContext); the WebMCP attach
  // stays feature-gated as before. DEVIATION FROM "one shared object reference": the registry holds
  // the tool object whose execute() THROWS on failure; the WebMCP path shares that object's name,
  // description, inputSchema, annotations and readOnlyHint by reference but wraps ONLY execute, so
  // the shim still gets today's error envelope (see adaptExecute below).
  var VMS_PAGE_TOOLS = (typeof window !== 'undefined' && window)
    ? (window.VMSPageTools = window.VMSPageTools || { version: 1, tools: [] })
    : null;
  if (VMS_PAGE_TOOLS && !VMS_PAGE_TOOLS.register) {
    VMS_PAGE_TOOLS.register = function (tool) {
      if (!tool || typeof tool.name !== 'string') return;
      var idx = -1;
      for (var i = 0; i < VMS_PAGE_TOOLS.tools.length; i++) { if (VMS_PAGE_TOOLS.tools[i].name === tool.name) { idx = i; break; } }
      if (idx >= 0) VMS_PAGE_TOOLS.tools[idx] = tool; else VMS_PAGE_TOOLS.tools.push(tool);
      try { document.dispatchEvent(new CustomEvent('vms:page-tools-changed')); } catch (e) { /* old browser or no document: the registration still stands */ }
    };
    VMS_PAGE_TOOLS.unregister = function (name) {
      var before = VMS_PAGE_TOOLS.tools.length;
      var kept = [];
      for (var i = 0; i < VMS_PAGE_TOOLS.tools.length; i++) { if (VMS_PAGE_TOOLS.tools[i].name !== name) kept.push(VMS_PAGE_TOOLS.tools[i]); }
      VMS_PAGE_TOOLS.tools = kept;
      if (VMS_PAGE_TOOLS.tools.length !== before) { try { document.dispatchEvent(new CustomEvent('vms:page-tools-changed')); } catch (e) { /* no document: ignore */ } }
    };
    VMS_PAGE_TOOLS.list = function () { return VMS_PAGE_TOOLS.tools.slice(); };
  }

  var ORIGIN = 'https://videomountstore.com';

  // MCP tool result: a content array with one text part. Objects are JSON.stringify'd (agents read the text).
  function textResult(payload) {
    var text = (typeof payload === 'string') ? payload : JSON.stringify(payload);
    return { content: [{ type: 'text', text: text }] };
  }

  // A dataset is usable only when it carries the shapes every tool reads: concerns[] and products[] arrays and
  // a sizes[] array. A bare {} or [] (or any other malformed value) is treated as ABSENT so a tool reports the
  // plain not-on-this-page error instead of throwing on undefined.
  function validShape(d) {
    return !!d && typeof d === 'object' && Array.isArray(d.concerns) && Array.isArray(d.products)
      && Array.isArray(d.sizes);
  }

  // DATA ACCESS. Prefer a page-provided window.VMSTvcmData object (tests inject it, and the page could set it),
  // but only when it is well shaped; otherwise parse the #vms-tvcm-data JSON block ONCE through
  // document.getElementById and cache the result (a valid object, or null). window.VMSTvcmData is checked first
  // at every call, so a value set after a failed parse still wins. Returns null when neither source yields a
  // well-shaped dataset, and every tool then reports it plainly.
  var parsedCache;   // undefined = not yet attempted; object or null once attempted
  function data() {
    if (typeof window !== 'undefined' && validShape(window.VMSTvcmData)) return window.VMSTvcmData;
    if (parsedCache !== undefined) return parsedCache;
    parsedCache = null;
    try {
      var doc = (typeof window !== 'undefined' && window.document) || ((typeof document !== 'undefined') ? document : null);
      var el = (doc && typeof doc.getElementById === 'function') ? doc.getElementById('vms-tvcm-data') : null;
      var txt = el ? (el.textContent || el.innerHTML || '') : '';
      if (txt) { var parsed = JSON.parse(txt); if (validShape(parsed)) parsedCache = parsed; }
    } catch (e) { parsedCache = null; }
    return parsedCache;
  }

  // The finder's public read-only page API (assets/vms-tvcm.js). Present only when the finder's JS ran. When it
  // is missing, find-mounts falls back to the snapshot and says so.
  function page() { return (typeof window !== 'undefined' && window.VMSTvcmPage) || null; }

  // [PAGE TOOLS 2026-09-11] A missing dataset THROWS a clear customer-safe message (never a partial or
  // fabricated result). On the WebMCP path adaptExecute turns the throw back into the error envelope;
  // on the registry path the throw reaches the chat's page script as a rejection.
  function noData() { throw new Error('The ceiling-mount finder data is not on this page.'); }

  // [PAGE TOOLS 2026-09-11] WebMCP execute adapter. On the registry path the chat's page script
  // calls a tool's execute() directly, so a failure must THROW (a synchronous throw or a rejected
  // Promise) for the chat to tell failure from success. The WebMCP shim, though, expects today's
  // error ENVELOPE, so here (and ONLY here) a thrown or rejected execute is turned back into
  // textResult(message). A successful execute (a sync envelope, or a Promise of one) passes through
  // unchanged, so a read-only tool stays synchronous for the shim.
  function errMessage(e) { return (e && e.message) ? e.message : 'This tool could not run right now.'; }
  function adaptExecute(t) {
    return function (input) {
      try {
        var out = t.execute(input);
        if (out && typeof out.then === 'function') { return out.then(function (r) { return r; }, function (e) { return textResult(errMessage(e)); }); }
        return out;
      } catch (e) { return textResult(errMessage(e)); }
    };
  }

  // Normalize a handle or SKU for matching: uppercase, strip everything but letters and digits, so
  // "33ce016", "33CE016" and "33-CE-016" all match, and a handle with hyphens equals its unpunctuated form.
  function norm(s) { return String(s).toUpperCase().replace(/[^A-Z0-9]/g, ''); }
  function absUrl(u) { return (typeof u === 'string' && u.indexOf('/') === 0) ? ORIGIN + u : u; }
  function strParam(v) { return (typeof v === 'string') ? v : null; }
  function isInt(n) { return (typeof n === 'number' && isFinite(n) && Math.floor(n) === n); }

  // [FIX 4] The size domain: prefer the finder's own sizes array (window.VMSTvcmPage.sizes, already NaN-filtered
  // in assets/vms-tvcm.js) when the page JS is present, and fall back to the snapshot's sizes otherwise. This
  // means a stray blank token that slipped into the snapshot (and rendered as a 0) never becomes the min once
  // the page is live; the section also skips blank tokens now, so the snapshot itself carries no 0.
  function sizeDomain(d) {
    var p = page();
    if (p && Array.isArray(p.sizes) && p.sizes.length) return p.sizes;
    return Array.isArray(d.sizes) ? d.sizes : [];
  }
  function sizeMin(d) { var a = sizeDomain(d); return a.length ? Math.min.apply(null, a) : null; }
  function sizeMax(d) { var a = sizeDomain(d); return a.length ? Math.max.apply(null, a) : null; }
  function concernIds(d) { return d.concerns.map(function (c) { return c.id; }); }
  function contentOnlyIds(d) { return d.concerns.filter(function (c) { return c.content_only; }).map(function (c) { return c.id; }); }
  function labelFor(d, id) { for (var i = 0; i < d.concerns.length; i++) { if (d.concerns[i].id === id) return d.concerns[i].label; } return id; }
  function formatPrice(n) { return (typeof n === 'number' && isFinite(n)) ? n.toFixed(2) : String(n); }
  function joinAnd(arr) {
    if (!arr.length) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return arr[0] + ' and ' + arr[1];
    return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
  }

  // [R3] The host does NOT enforce inputSchema (a string size and an unknown key both
  // reached the code, measured on live), so every tool refuses top-level keys it does
  // not accept, naming the ones it does. Returns the array of unexpected keys.
  function unknownKeys(args, allowed) {
    var extra = [];
    if (args && typeof args === 'object') {
      for (var k in args) {
        if (Object.prototype.hasOwnProperty.call(args, k) && allowed.indexOf(k) === -1) extra.push(k);
      }
    }
    return extra;
  }

  // [R3] The page-level config the snapshot carries (absolute builder / angled-wall /
  // VESA-adapter URLs, the phone, the VESA fallback copy). Always an object.
  function pageConfig(d) { return (d && d.page_config && typeof d.page_config === 'object') ? d.page_config : {}; }
  function phoneOf(d) { var pc = pageConfig(d); return (typeof pc.phone === 'string' && pc.phone) ? pc.phone : '800-585-8795'; }
  function builderUrlOf(d) { var pc = pageConfig(d); return pc.builder_url ? absUrl(pc.builder_url) : (ORIGIN + '/pages/build-custom-tv-ceiling-mount'); }
  function angledUrlOf(d) { var pc = pageConfig(d); return pc.angled_wall_url ? absUrl(pc.angled_wall_url) : (ORIGIN + '/pages/tv-mount-for-slanted-wall'); }

  // [R3] The two nearest sizes to a size that is not on the page's made-size list, the
  // way the page's own size hint does it (closestSizes in assets/vms-tvcm.js), computed
  // from the same sizes data. Order-independent (never assumes the array is sorted).
  function closestSizes(domain, size) {
    var below = null, above = null;
    for (var i = 0; i < domain.length; i++) {
      var v = domain[i];
      if (typeof v !== 'number') continue;
      if (v < size && (below === null || v > below)) below = v;
      if (v > size && (above === null || v < above)) above = v;
    }
    var out = [];
    if (below !== null) out.push(below);
    if (above !== null) out.push(above);
    return out;
  }

  // [R3] The absolute URL the finder page would parse for these filters (readUrlState in
  // assets/vms-tvcm.js reads concerns, size and vesa off the query string), so the agent
  // can hand the shopper a link to the same filtered view instead of flipping chips.
  function pageUrlWithFilters(d, size, passedConcerns, vesa) {
    var base = (d && typeof d.page_url === 'string' && d.page_url) ? d.page_url : (ORIGIN + '/collections/tv-ceiling-mounts');
    var qp = [];
    if (passedConcerns && passedConcerns.length) qp.push('concerns=' + encodeURIComponent(passedConcerns.join(',')));
    if (size != null) qp.push('size=' + encodeURIComponent(String(size)));
    if (vesa != null) qp.push('vesa=' + encodeURIComponent(vesa));
    return qp.length ? (base + '?' + qp.join('&')) : base;
  }

  // [R3] page_notes[] a shopper-helping agent can relay: the builder link always; the
  // Low + Angled combo message (angled wall builder plus the phone) when both are asked;
  // the VESA adapter-plate fallback when a VESA pattern was given and nothing matched.
  function buildPageNotes(d, concerns, vesa, count) {
    var notes = [];
    var builderUrl = builderUrlOf(d);
    notes.push({ id: 'builder', url: builderUrl, text: 'To build a complete flat-ceiling mount (plate, pipe and head) to an exact finished length, use the builder at ' + builderUrl + '.' });
    if (concerns.indexOf('low') !== -1 && concerns.indexOf('angled') !== -1) {
      var angledUrl = angledUrlOf(d);
      // [R3 item 5] Prefer the owner-editable combo copy (combo_body from the section
      // settings, the SAME text the visible Low + Angled box shows); the constructed
      // sentence is only the fallback default. The url always carries the builder link.
      var pcCombo = pageConfig(d);
      var comboText = (typeof pcCombo.combo_body === 'string' && pcCombo.combo_body)
        ? pcCombo.combo_body
        : ('A low, angled ceiling is often a slanted wall. Build it on the angled wall TV mount builder at ' + angledUrl + ', or call ' + phoneOf(d) + '.');
      notes.push({ id: 'combo', url: angledUrl, text: comboText });
    }
    if (vesa != null && count === 0) {
      var pc = pageConfig(d);
      var vurl = pc.vesa_adapter_url ? absUrl(pc.vesa_adapter_url) : null;
      var body = (typeof pc.vesa_fallback_body === 'string' && pc.vesa_fallback_body) ? pc.vesa_fallback_body : 'A VESA adapter plate bridges your TV to the mount when the holes do not line up.';
      var note = { id: 'vesa_fallback', text: body + (vurl ? (' Shop VESA adapter plates at ' + vurl + '.') : (' Call ' + phoneOf(d) + '.')) };
      if (vurl) note.url = vurl;
      notes.push(note);
    }
    return notes;
  }

  // The SAME filter rule the finder's matchesWith runs, computed from a snapshot product: the size within the
  // product's screen_low..screen_high (a blank bound excludes the product when a size is asked, exactly as the
  // card's parseInt(NaN) comparison does), the vesa pattern in the product's vesa list, and every FILTERING
  // concern present in the product's chips (content-only ids are removed by the caller before this runs). Used
  // only for the fallback when the page API is absent; when the page API is present its findMounts is called
  // instead and returns the same handles.
  function snapshotMatch(prod, size, vesa, filteringConcerns) {
    if (size != null) {
      var lo = prod.screen_low, hi = prod.screen_high;
      if (lo == null || hi == null || !(lo <= size && size <= hi)) return false;
    }
    if (vesa != null && (prod.vesa || []).indexOf(vesa) === -1) return false;
    for (var i = 0; i < filteringConcerns.length; i++) {
      if ((prod.chips || []).indexOf(filteringConcerns[i]) === -1) return false;
    }
    return true;
  }

  // A match record for find-mounts: the card facts, with an absolute product url. price_max only when it
  // differs from price (the snapshot omits it otherwise).
  function mountObj(prod) {
    var o = {
      handle: prod.handle,
      sku: prod.sku,
      title: prod.title,
      url: absUrl(prod.url),
      price: prod.price,
      screen_low: (prod.screen_low != null) ? prod.screen_low : null,
      screen_high: (prod.screen_high != null) ? prod.screen_high : null,
      chips: prod.chips || [],
      vesa: prod.vesa || [],
      reach_min_in: (prod.reach_min_in != null) ? prod.reach_min_in : null,
      reach_max_in: (prod.reach_max_in != null) ? prod.reach_max_in : null,
      reach_available: !!prod.reach_available,
      plate_deltas: prod.plate_deltas || null,
      weight_capacity_lb: (prod.weight_capacity_lb != null) ? prod.weight_capacity_lb : null,
      product_type: (prod.product_type != null) ? prod.product_type : null,
      configurable: !!prod.configurable,
      image: (prod.image != null) ? prod.image : null
    };
    // [R3] variant_sku only when the snapshot carries it (the configurable mounts). sku
    // is the base SKU an agent's user says ("33CE016"); variant_sku is the full string.
    if (prod.variant_sku != null) o.variant_sku = prod.variant_sku;
    if (prod.price_max != null) o.price_max = prod.price_max;
    return o;
  }

  var tools = [
    {
      name: 'vms-list-concerns',
      annotations: { readOnlyHint: true },
      readOnlyHint: true,
      description: 'List the concern chips the TV ceiling-mount finder offers, so you know what to pass to vms-find-mounts. Returns each concern with its id, on-page label, whether it filters, whether it is content-only, its guidance text and HTML, and the inch rule for the high and low chips. Also returns the screen-size range the finder accepts (inches), the VESA patterns it knows, and the mount count. Two concerns (truss, wood) show guidance but do not narrow the list. Takes no parameters. Read-only.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      execute: function (args) {
        var extra = unknownKeys(args, []);
        if (extra.length) throw new Error('list-concerns takes no parameters, but received: ' + extra.join(', ') + '.');
        var d = data(); if (!d) return noData();
        return textResult({
          concerns: d.concerns.map(function (c) { return { id: c.id, label: c.label, content_only: !!c.content_only, filters: !!c.filters, content: (c.content != null ? c.content : null), content_html: (c.content_html != null ? c.content_html : null), rule: (c.rule != null ? c.rule : null) }; }),
          screen_size_min_in: sizeMin(d),
          screen_size_max_in: sizeMax(d),
          vesa_patterns: d.vesa_patterns || [],
          product_count: d.products.length,
          note: 'The two content-only concerns (truss, wood) show guidance on the page but do not narrow the list.'
        });
      }
    },
    {
      name: 'vms-find-mounts',
      annotations: { readOnlyHint: true },
      readOnlyHint: true,
      description: 'Find the TV ceiling mounts on this page that fit a TV size and a set of concerns (the same filter the chips run). All parameters are optional; with none it returns every mount. Pass the TV diagonal in inches (screen_size_in), concern ids from vms-list-concerns, and an optional VESA pattern (vesa). A size out of range or an unknown concern or VESA is refused. Returns the count, the matches in page order, a hand-back line, page notes, a filtered URL and builder links. Read-only.',
      inputSchema: {
        type: 'object',
        properties: {
          screen_size_in: { type: 'integer', description: 'Optional. The TV diagonal in inches, a whole number within the size range list-concerns returns (smallest to largest). A size outside that range is refused.' },
          concerns: { type: 'array', items: { type: 'string' }, description: 'Optional. An array of concern ids from list-concerns (for example ["angled","low"]). An unknown id is refused. The content-only ids (truss, wood) are accepted and ignored for filtering.' },
          vesa: { type: 'string', description: 'Optional. A VESA mounting-hole pattern, one of the patterns list-concerns returns (width x height in mm, for example "400x400"). An unknown pattern is refused.' }
        },
        additionalProperties: false
      },
      execute: function (args) {
        var a = args || {};
        var extra = unknownKeys(a, ['screen_size_in', 'concerns', 'vesa']);
        if (extra.length) throw new Error('These keys are not accepted: ' + extra.join(', ') + '. find-mounts accepts screen_size_in, concerns and vesa.');
        var d = data(); if (!d) return noData();

        var smin = sizeMin(d), smax = sizeMax(d);
        var size = null;
        var sizeNote = null;
        if (a.screen_size_in != null) {
          if (!isInt(a.screen_size_in)) {
            throw new Error('The screen size must be a whole number of inches' + (smin != null ? (' from ' + smin + ' to ' + smax + '.') : '.'));
          }
          if (smin != null && (a.screen_size_in < smin || a.screen_size_in > smax)) {
            throw new Error('The screen size must be from ' + smin + ' to ' + smax + ' inches. That is the range this finder covers.');
          }
          size = a.screen_size_in;
          // [R3] A real number inside the range but not a made TV size: accept it and add
          // a note naming the nearest made sizes, mirroring the page's own size hint.
          var domain = sizeDomain(d);
          if (domain.length && domain.indexOf(size) === -1) {
            var near = closestSizes(domain, size);
            if (near.length) sizeNote = 'No ' + size + ' inch TVs were made. The closest made sizes are ' + joinAnd(near.map(String)) + '.';
          }
        }

        var concerns = [];     // filtering concerns only (content-only removed)
        var ignored = [];      // content-only ids the caller passed, accepted and ignored
        var passedValid = [];  // [R3] every valid concern the caller passed, in order, for the shareable page URL
        if (a.concerns != null) {
          if (!Array.isArray(a.concerns)) throw new Error('The concerns must be an array of concern ids. Call list-concerns for the valid ids.');
          var validIds = concernIds(d);
          var coIds = contentOnlyIds(d);
          for (var i = 0; i < a.concerns.length; i++) {
            var cid = a.concerns[i];
            if (typeof cid !== 'string' || validIds.indexOf(cid) === -1) {
              throw new Error('"' + cid + '" is not a concern on this page. The valid concern ids are: ' + validIds.join(', ') + '.');
            }
            if (passedValid.indexOf(cid) === -1) passedValid.push(cid);
            if (coIds.indexOf(cid) !== -1) { if (ignored.indexOf(cid) === -1) ignored.push(cid); }
            else if (concerns.indexOf(cid) === -1) { concerns.push(cid); }
          }
        }

        var vesa = null;
        if (a.vesa != null) {
          var vRaw = strParam(a.vesa);
          if (vRaw === null) throw new Error('The VESA pattern must be a text value, for example "400x400", or leave it out.');
          // [FIX 5] Normalize the agent's value (lowercase, strip all whitespace) before matching, so
          // "400 x 400", "400X400" and " 400x400 " all resolve to the on-page pattern "400x400". The
          // canonical spelling from the page is what carries forward (the product vesa lists use it).
          var vNorm = vRaw.toLowerCase().replace(/\s+/g, '');
          var patterns = d.vesa_patterns || [];
          var vCanon = null;
          for (var vi = 0; vi < patterns.length; vi++) {
            if (String(patterns[vi]).toLowerCase().replace(/\s+/g, '') === vNorm) { vCanon = patterns[vi]; break; }
          }
          if (vCanon === null) throw new Error('"' + vRaw + '" is not a VESA pattern on this page. The valid patterns are: ' + patterns.join(', ') + '.');
          vesa = vCanon;
        }

        // The page API (the shipped finder JS) when present; the snapshot rule otherwise. Both remove content-
        // only concerns and produce the same handle set; we re-order through d.products so either path returns
        // the matches in page priority order.
        var p = page();
        var computedFrom, handleList;
        if (p && typeof p.findMounts === 'function') {
          computedFrom = 'page';
          handleList = p.findMounts({ size: size, vesa: vesa, concerns: concerns });
        } else {
          computedFrom = 'snapshot';
          handleList = d.products.filter(function (prod) { return snapshotMatch(prod, size, vesa, concerns); })
            .map(function (prod) { return prod.handle; });
        }
        var inSet = {};
        for (var h = 0; h < handleList.length; h++) { inSet[handleList[h]] = 1; }
        var matchedProducts = d.products.filter(function (prod) { return inSet[prod.handle]; });
        var matches = matchedProducts.map(mountObj);
        // [R3] why per match: the concerns that matched (every filtering concern passed,
        // since the product cleared them all) and the product's screen range, so the
        // agent can say WHY a mount is in the list.
        matches.forEach(function (mm) {
          mm.why = {
            matched_concerns: concerns.slice(),
            size_range: (mm.screen_low != null && mm.screen_high != null) ? (mm.screen_low + ' to ' + mm.screen_high) : null
          };
        });

        var count = matches.length;
        var total = d.products.length;
        var cheapest = null;
        for (var m = 0; m < matches.length; m++) {
          if (cheapest === null || matches[m].price < cheapest.price) cheapest = matches[m];
        }

        var concernLabels = concerns.map(function (id) { return labelFor(d, id); });
        var subject;
        if (size != null && concernLabels.length) subject = 'a ' + size + ' inch TV with ' + joinAnd(concernLabels);
        else if (size != null) subject = 'a ' + size + ' inch TV';
        else if (concernLabels.length) subject = joinAnd(concernLabels);
        else subject = null;
        if (vesa != null) {
          subject = (subject ? (subject + ' and ') : '') + 'a ' + vesa + ' VESA pattern';
        }

        // [R3] The plate a concern implies, so "cheapest" does not quote the flat-plate
        // price for a build that needs the angled or unistrut plate.
        var plateWord = (concerns.indexOf('angled') !== -1) ? 'angled' : ((concerns.indexOf('unistrut') !== -1) ? 'unistrut' : null);

        var handback;
        if (subject === null) {
          handback = 'All ' + total + ' ceiling mounts are shown. Add a screen size or a concern to narrow the list.';
        } else if (count === 0) {
          handback = 'No mount on this page fits ' + subject + '. Call ' + phoneOf(d) + ' and we will help.';
        } else {
          handback = count + ' of ' + total + ' ceiling mounts fit ' + subject + '.';
          if (cheapest) {
            if (plateWord) {
              var pd = cheapest.plate_deltas;
              var delta = (pd && pd[plateWord] != null) ? pd[plateWord] : null;
              // [R3 item 6] Quote the flat plate's OWN cheapest price, not the product
              // minimum (which could be a No Mast or a non-flat variant on some product).
              var flatPrice = (pd && pd.flat != null) ? pd.flat : cheapest.price;
              var plateLabel = (plateWord === 'angled') ? 'angled plate' : 'unistrut plate';
              handback += ' ' + cheapest.title + ' starts at $' + formatPrice(flatPrice) + ' with a flat plate; the ' + plateLabel + (delta != null ? (' adds $' + formatPrice(delta) + '.') : ' costs more on the product page.');
            } else {
              handback += ' The cheapest is ' + cheapest.title + ' at $' + formatPrice(cheapest.price) + '.';
            }
          }
        }

        var out = {
          count: count,
          total: total,
          screen_size_in: size,
          concerns: concerns,
          vesa: vesa,
          computed_from: computedFrom,
          matches: matches,
          handback: handback,
          page_notes: buildPageNotes(d, concerns, vesa, count),
          page_url_with_filters: pageUrlWithFilters(d, size, passedValid, vesa)
        };
        if (sizeNote) out.size_note = sizeNote;
        // [R3] Builder hand-off for the length question the chips page cannot answer:
        // when high, low or angled is in play, point the agent at the two builders that
        // take an exact finished length.
        if (concerns.indexOf('high') !== -1 || concerns.indexOf('low') !== -1 || concerns.indexOf('angled') !== -1) {
          var bUrl = builderUrlOf(d), aUrl = angledUrlOf(d);
          out.builder_handoff = {
            configurator_url: bUrl,
            configurator_note: 'For an exact finished length, build it on the flat-ceiling builder at ' + bUrl + '.',
            angled_wall_url: aUrl,
            angled_wall_note: 'For a slanted or angled ceiling, build it on the angled wall builder at ' + aUrl + '.'
          };
        }
        if (ignored.length) {
          out.ignored_content_only = ignored;
          out.ignored_note = 'These concerns show guidance on the page but do not narrow the list, so they were not applied: ' + ignored.join(', ') + '.';
        }
        return textResult(out);
      }
    },
    {
      name: 'vms-get-mount',
      annotations: { readOnlyHint: true },
      readOnlyHint: true,
      description: 'Get the full card record for one TV ceiling mount by its handle or SKU. Get the value from your user or a vms-find-mounts result; pass the handle as returned, the base SKU ("33CE016"), or the full variant SKU. Punctuation and case are ignored, but the match is exact. Returns the card facts (title, SKU, link, price, screen and reach ranges, chips, VESA and hole ranges, plate deltas, weight, types) and a sentence. An unknown value returns found false with the page\'s handles. Read-only.',
      inputSchema: {
        type: 'object',
        properties: {
          mount: { type: 'string', description: 'A product handle or a SKU, 1 to 120 characters (for example the handle "33ce016-tv-ceiling-mount-for-32-70-with-tilt-rotate-adjustable-mast" or the base SKU "33CE016"). Punctuation and spacing are ignored when matching; the match is exact.' }
        },
        required: ['mount'],
        additionalProperties: false
      },
      execute: function (args) {
        var a = args || {};
        var extra = unknownKeys(a, ['mount']);
        if (extra.length) throw new Error('These keys are not accepted: ' + extra.join(', ') + '. get-mount accepts only mount (a handle or a SKU).');
        var d = data(); if (!d) return noData();
        var mRaw = strParam(a.mount);
        if (mRaw === null) throw new Error('The mount must be a text value: a product handle or a SKU, for example "33CE016".');
        var m = mRaw.trim();
        if (m.length < 1 || m.length > 120) throw new Error('The mount must be 1 to 120 characters: a product handle or a SKU, for example "33CE016".');
        var q = norm(m);
        if (q === '') throw new Error('The mount must be 1 to 120 characters: a product handle or a SKU, for example "33CE016".');

        // [FIX 6 / R3] Collect EVERY product whose normalized base SKU, full variant SKU or handle equals the
        // query, not just the first. A handle and a SKU on different products can normalize to the same string,
        // and the agent should be told to disambiguate with the exact handle rather than silently getting
        // whichever card came first.
        var hits = [];
        for (var i = 0; i < d.products.length; i++) {
          var pr = d.products[i];
          if (norm(pr.handle) === q || (pr.sku && norm(pr.sku) === q) || (pr.variant_sku && norm(pr.variant_sku) === q)) hits.push(pr);
        }
        if (hits.length === 0) {
          return textResult({
            found: false,
            query: m,
            message: 'No mount matching "' + m + '" is on this page. Check the handle or SKU; the mounts on this page are listed below.',
            handles: d.products.map(function (p2) { return p2.handle; }),
            skus: d.products.map(function (p2) { return p2.sku; }).filter(Boolean)
          });
        }
        if (hits.length > 1) {
          return textResult({
            found: true,
            ambiguous: true,
            query: m,
            message: 'More than one mount on this page matches "' + m + '". Use the exact handle to get a single record; the matching mounts are listed here.',
            matches: hits.map(mountObj)
          });
        }
        var prod = hits[0];

        var chipDetails = (prod.chips || []).map(function (id) { return { id: id, label: labelFor(d, id) }; });

        var sentence = prod.title + (prod.sku ? (' (' + prod.sku + ')') : '');
        if (prod.screen_low != null && prod.screen_high != null) {
          sentence += ' fits TVs from ' + prod.screen_low + ' to ' + prod.screen_high + ' inch';
        }
        if (prod.weight_capacity_lb != null) {
          sentence += (prod.screen_low != null && prod.screen_high != null ? ' and' : '') + ' holds up to ' + prod.weight_capacity_lb + ' lb';
        }
        sentence += '. It is $' + formatPrice(prod.price) + '.';

        var rec = {
          found: true,
          handle: prod.handle,
          sku: prod.sku,
          title: prod.title,
          url: absUrl(prod.url),
          price: prod.price,
          priority: (prod.priority != null) ? prod.priority : null,
          screen_low: (prod.screen_low != null) ? prod.screen_low : null,
          screen_high: (prod.screen_high != null) ? prod.screen_high : null,
          chips: chipDetails,
          vesa: prod.vesa || [],
          vesa_width_mm: prod.vesa_width_mm || null,
          vesa_height_mm: prod.vesa_height_mm || null,
          ceiling_distance_min_in: (prod.ceiling_distance_min_in != null) ? prod.ceiling_distance_min_in : null,
          ceiling_distance_max_in: (prod.ceiling_distance_max_in != null) ? prod.ceiling_distance_max_in : null,
          reach_min_in: (prod.reach_min_in != null) ? prod.reach_min_in : null,
          reach_max_in: (prod.reach_max_in != null) ? prod.reach_max_in : null,
          reach_available: !!prod.reach_available,
          plate_deltas: prod.plate_deltas || null,
          weight_capacity_lb: (prod.weight_capacity_lb != null) ? prod.weight_capacity_lb : null,
          product_type: (prod.product_type != null) ? prod.product_type : null,
          compatibility: prod.compatibility || [],
          configurable: !!prod.configurable,
          image: (prod.image != null) ? prod.image : null,
          sentence: sentence
        };
        if (prod.variant_sku != null) rec.variant_sku = prod.variant_sku;
        if (prod.price_max != null) rec.price_max = prod.price_max;
        return textResult(rec);
      }
    }
  ];

  // [PAGE TOOLS 2026-09-11] Publish the shared tool objects on the registry now, ungated and in the
  // listed order (the important tools first). This does NOT wait for modelContext and does NOT need
  // the page object; a tool whose page object is not ready throws a clear message when executed.
  if (VMS_PAGE_TOOLS) { for (var pti = 0; pti < tools.length; pti++) { VMS_PAGE_TOOLS.register(tools[pti]); } }

  // [MCP 2026-09-08] Register every tool against an MC-like object (the real modelContext or a test stub).
  // IDEMPOTENT PER TARGET: a target already registered against is skipped (a duplicate registerTool may throw
  // InvalidStateError in Chrome 151), so retries and a manual second call never double-register. Each
  // registerTool is wrapped in its own try, so one tool the host rejects does not abort the rest; the count of
  // successes is returned. A DIFFERENT target (a test stub) is always attempted. unregisterTool is never
  // called (Chrome 151's modelContext has no such method), so nothing here depends on it.
  var attachedTargets = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;
  function attach(mc) {
    if (!mc || typeof mc.registerTool !== 'function') return 0;
    if (attachedTargets && attachedTargets.has(mc)) return 0;   // idempotent: already attached to this target
    var n = 0;
    for (var i = 0; i < tools.length; i++) {
      var t = tools[i];
      try {
        // [PAGE TOOLS 2026-09-11] Share name / description / inputSchema / annotations by reference and
        // wrap ONLY execute so the shim keeps getting today's error envelope on a failure. readOnlyHint is
        // added only when true, never as an explicit undefined on a mutating tool.
        var reg = { name: t.name, description: t.description, inputSchema: t.inputSchema, annotations: t.annotations, execute: adaptExecute(t) };
        if (t.readOnlyHint === true) reg.readOnlyHint = true;
        if (t.outputSchema) reg.outputSchema = t.outputSchema;
        mc.registerTool(reg);
        n++;
      } catch (e) { /* a tool the host rejects (or already-registered): keep the rest */ }
    }
    if (n > 0 && attachedTargets) attachedTargets.add(mc);   // mark attached only after a successful pass
    return n;
  }

  if (typeof window !== 'undefined') {
    window.VMSTvcmMCP = { tools: tools, attach: attach };
    // [MCP 2026-09-08] PATIENT, IDEMPOTENT AUTO-ATTACH, copied in behaviour from the shipped sibling layers.
    // Measured on the dev preview in Chrome 151 with the WebMCP flag ON: BOTH document.modelContext and
    // navigator.modelContext exist, and Shopify's own platform already registers TEN native storefront tools
    // (browse_store, get_cart, search_catalog, proceed_to_checkout, ...) in the same registry; our three
    // kebab-case tools coexist beside those snake_case natives. Critically, Chrome injects modelContext AFTER
    // this deferred script evaluates, so the old one-shot attach at load silently no-opped and the page
    // reported "no tools". This modelContext also has NO unregisterTool. So: re-resolve
    // document/navigator.modelContext at EACH attempt (never cache a null), and retry on DOMContentLoaded, on
    // window load, then on a timer backoff, giving up after about 31s. The whole thing is gated to a secure,
    // top-level page and is a silent no-op with no modelContext, so a normal shopper's browser is completely
    // unaffected. Reads window.document / window.navigator (identical to the bare globals in a browser) so a
    // test can inject a late modelContext.
    var done = false;
    function currentMC() {
      var doc = (window.document) || ((typeof document !== 'undefined') ? document : null);
      var nav = (window.navigator) || ((typeof navigator !== 'undefined') ? navigator : null);
      return (doc && doc.modelContext) || (nav && nav.modelContext) || null;
    }
    function tryAttachNow() {
      if (done) return true;
      var topLevel = (window.top === window.self);
      var secure = (typeof window.isSecureContext === 'undefined') ? true : !!window.isSecureContext;
      if (!topLevel || !secure) { done = true; return true; }   // never attach in a frame / insecure page
      var mc = currentMC();
      if (!mc || typeof mc.registerTool !== 'function') return false;   // modelContext not injected yet: retry
      attach(mc);
      done = true;   // it appeared and we attached (idempotent); stop the retry schedule
      return true;
    }
    try {
      if (!tryAttachNow()) {
        var onEvent = function () { tryAttachNow(); };
        if (window.document && window.document.addEventListener) { window.document.addEventListener('DOMContentLoaded', onEvent); }
        if (window.addEventListener) { window.addEventListener('load', onEvent); }
        var intervals = [500, 500, 1000, 2000, 4000, 8000, 15000];   // cumulative about 31s, then give up
        var scheduleRetry = function (i) {
          if (done || i >= intervals.length || typeof window.setTimeout !== 'function') return;
          window.setTimeout(function () { if (!tryAttachNow()) scheduleRetry(i + 1); }, intervals[i]);
        };
        scheduleRetry(0);
      }
    } catch (e) { /* scheduling APIs missing (e.g. a bare test window): the manual attach path still works */ }
  }
})();
